<template>
  <div class="reporting__move">
    <slot></slot>
    <h3>
      <span class="title">{{ $t('reporting.move.title.normal') }}</span>
      <span class="sub">{{ $t('reporting.move.title.small') }}</span>
    </h3>

    <div v-if="hasData"
         class="reporting__export"
         :class="currentLang"
         @click="onExcelExport">
      <i class="icon icon-excel-size16"> </i>
    </div>

    <!-- no data -->
    <div v-if="!hasData || !isDesktopDevice" class="no-data">
      <p v-if="!isDesktopDevice">{{ $t('common.inReady') }}</p>
      <p v-else v-html="$t('reporting.move.noHoverData')"></p>
    </div>
    <div v-else>
      <!-- ranking summary -->
      <div class="reporting__move__rank-summary content-box">
        <div>
          <div class="reporting__move__rank-summary-title">
            <span class="title" v-html="$t('reporting.move.subTitle')"></span>
            <ui-icon-tooltip>{{ $t('reporting.move.desc') }}</ui-icon-tooltip>
            <div class="more-info" @click="showRankList = true">
              <i class="icon icon--more-info"></i>
              <span>{{ $t('common.more') }}</span>
            </div>
          </div>
          <p class="notification" v-html="$t('reporting.move.notice')"></p>
          <div class="reporting__move__rank-summary-chart">
            <app-chart chartType="bar" v-if="summaryChartOptions"
              :chartOptions="summaryChartOptions"
              width="100%"
              height="180px"/>
          </div>
        </div>
      </div>

      <!-- ranking list -->
      <div class="reporting__move__rank-list">
        <ul>
          <top-rank-item v-for="(item, index) in topItems" :key="index"
            :imageData="imageData"
            :item="item"
            :index="index"
            type="hover"
            :isDesktopDevice="isDesktopDevice"
            @onThumbnailClick="highlightCoords"/>
        </ul>
      </div>

      <!-- more info -->
      <rank-list v-if="showRankList"
                 type="hover"
                 :rankList="rankList"
                 :imageData="imageData"
                 :isDesktopDevice="isDesktopDevice"
                 @onCloseClick="showRankList = false"/>

      <slot name="footer"/>
    </div>
  </div>
</template>

<script>
import { parse } from 'date-fns'
import { thousand } from '@/libs/utils'
import { getTooltipHtml } from '@/libs/chart-helper'
import Excel from '@/libs/excel-export'
import TopRankItem from './TopRankItem'
import RankList from './RankList'
import AppChart from '@/components/chart'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import { mapState } from 'vuex'

export default {
  name: 'ReportMove',
  components: {
    TopRankItem,
    RankList,
    AppChart,
    UiIconTooltip
  },
  props: [
    'isLoading',
    'reportName',
    'reportData',
    'infoData',
    'referer',
    'metric',
    'activeSelector',
    'hourData',
    'freqPv',
    'freqTotalPv',
    'freqOption'
  ],
  data () {
    return {
      showRankList: false
    }
  },
  computed: {
    ...mapState([
      'applyData'
    ]),
    currentLang () {
      return this.$i18n.locale
    },
    isDesktopDevice () {
      return this.reportData.device === 'desktop'
    },
    imageData () {
      return this.reportData.image
    },
    rankList () {
      const sortColumn = 'hoverCnt'

      const items = Object.values(this.reportData.data.coords).sort((a, b) => {
        return b[sortColumn] - a[sortColumn]
      })

      return items
    },
    hasData () {
      return this.targetItems && this.targetItems.length
    },
    targetItems () {
      let items = null

      if (this.reportData && this.reportData.data) {
        items = this.reportData.data.topHovers
      }

      if (!items) {
        return null
      }

      return Array.from(items).sort((a, b) => {
        return b.hoverCnt - a.hoverCnt
      })
    },
    topItems () {
      if (!this.targetItems) {
        return []
      }
      return this.targetItems.map(item => {
        return {
          value: item.hoverCnt,
          coords: this.reportData.data.coords[item.dom],
          chartData: item.daily.map(dailyItem => {
            return [
              parse(dailyItem.date).getTime(),
              dailyItem.hover
            ]
          })
        }
      })
    },
    summaryChartOptions () {
      if (!this.targetItems) {
        return null
      }

      const chartData = this.targetItems.map((item, index) => {
        return [
          this.$options.filters.rank(index + 1),
          item.hoverCnt
        ]
      })

      return {
        xAxis: {
          right: 0
        },
        yAxis: {
          splitNumber: 2
        },
        series: [
          {
            name: this.$t('reporting.move.title.small'),
            type: 'bar',
            data: chartData,
            barWidth: 10,
            itemStyle: {
              color: '#ec0047',
              barBorderRadius: 5
            }
          }
        ],
        tooltip: {
          formatter (params) {
            const param = params[0]
            const title = param.data[0]
            const content = [
              [
                `${param.marker}${param.seriesName} :`,
                `${thousand(param.data[1])}`
              ]
            ]
            return getTooltipHtml(content, title)
          }
        }
      }
    }
  },
  created () {
  },
  methods: {
    onReportModeClick (value) {
      this.reportMode = value
      this.renderCharts()
    },
    renderCharts () {
      const rawData = this.reportData.data
      const rankChartData = []
      this.topData = []

      const targetData = rawData.topHovers

      if (!targetData || !targetData.length) {
        this.hasData = false
        return
      }

      this.hasData = true

      targetData.sort((a, b) => {
        return b.hoverCnt - a.hoverCnt
      })

      for (const [index, item] of targetData.entries()) {
        rankChartData.push([
          this.$options.filters.rank(index + 1),
          item.hoverCnt
        ])
        this.topData.push({
          value: item.hoverCnt,
          coords: rawData.coords[item.dom],
          chartData: item.daily.map(dailyItem => {
            return [
              parse(dailyItem.date).getTime(),
              dailyItem.hover
            ]
          })
        })
      }

      this.dailyChartOptions = {
        xAxis: {
          right: 0
        },
        yAxis: {
          splitNumber: 2
        },
        series: [
          {
            name: 'PV',
            type: 'bar',
            data: rankChartData,
            barWidth: 10,
            itemStyle: {
              color: '#ec0047',
              barBorderRadius: 5
            }
          }
        ]
      }
    },
    highlightCoords (item) {
      let selector = item.coords.selector

      if (selector === this.activeSelector) {
        selector = null
      }

      this.$emit('onActiveSelectorChange', selector)
    },
    onExcelExport () {
      let vue = this
      let fileName = this.applyData.pageName + '_' + this.reportName
      let excel = new Excel()
      vue.$emit('onDownloading', true)
      excel.setReportInfo(this.applyData, this.infoData, this.reportData, this.referer, this.metric, this.hourData, this.freqPv, this.freqTotalPv, this.freqOption)
        .then(
          excel.setReportData(this.reportName, this.reportData, this.rankList || [], this.reportData.image.url, { highlight: 'J' })
            .then(result => {
              excel.download(fileName)
              vue.$emit('onDownloading', false)
            })
        )
    }
  },
  destroyed () {
    this.$emit('onActiveSelectorChange', null)
  }
}
</script>

<style lang="scss">
  .reporting__move {
    position: relative;
    width: 100%;

    h3 {
      color: #444444;

      .title {
        font-size: 22px;
        font-weight: 500;
      }
      .sub {
        font-size: 15px;

        &:before {
          content: '';
          display: inline-block;
          width: 1px;
          height: 15px;
          border-left: 1px solid #d7d7d7;
          margin-left: 10px;
          padding-left: 10px;
        }
      }
    }

    .reporting__export {
      position: absolute;
      top: -5px;
      right: 0;
      font-size: 12px;
      border-radius: 100%;
      width: 34px;
      height: 34px;
      background-color: #fff;
      border: 1px solid #bbb;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-sizing: border-box;

      &:hover {
        &:after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border-radius: 100%;
          background-color: rgba(0, 0, 0, 0.06);
        }
      }

      &:active {
        &:after {
          background-color: rgba(0, 0, 0, 0.32);
        }
      }

      .icon {
        vertical-align: middle;
      }

      &.ja {
        padding-top: 1px;
      }
      &.en {
        .icon {
          transform: translateY(-1px);
        }
      }

      &.browser-ie {
        padding-top: 4px;

        .icon {
          transform: translateY(-1px);
        }

        &.en {
          padding-top: 2px;
        }
      }

      &.browser-safari {
        padding-top: 1px;

        .icon {
          transform: translateY(-1px);
        }

        &.en {
          padding-top: 2px;
        }
      }

      &.platform-window {
        &.en {
          padding-top: 1px;
        }
      }
    }

    .reporting__move__rank-summary {
      margin-top: 30px;

      .reporting__move__rank-summary-title {
        .title {
          font-size: 15px;
          font-weight: 500;
          color: #2f2f2f;
        }

        .tooltip {
          margin-left: 8px;
        }

        .more-info {
          float: right;
          cursor: pointer;
          i {
            margin-right: 4px;
            vertical-align: bottom;
          }
          span {
            font-weight: 600;
            color: #444444;
            line-height: 13px;
          }
        }
      }

      .notification {
        font-size: 12px;
        color: $brandColor;
        margin-top: 20px;
        font-weight: 500;
      }

      .reporting__move__rank-summary-chart {
        margin-top: 14px;
      }
    }

    .reporting__move__rank-list {
      .reporting__move__rank-list-item{
        margin-top: 30px;

        strong {
          font-size: 15px;
          .rank {
            color: #2f2f2f;
          }
          .value {
            color: $brandColor;
          }
        }

        .rank-list-item__element-info {
          margin-top: 13px;
          display: flex;
          justify-content: space-between;
          height: 75px;
          border-radius: 6px;
          border: solid 1px #d7d7d7;
          background-color: #f4f4f4;
          color: #666666;
          cursor: pointer;

          .rank-list-item__element-img {
            position: relative;
            width: calc(100% - 145px);

            .element-img--wrap {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
            }
          }

          .rank-list-item__element-detail {
            position: relative;
            width: 145px;

            ul {
              width: 100%;
              position: absolute;
              top: 50%;
              right: 50%;
              transform: translate(50%, -50%);
              font-size: 12px;
              color: #666666;
              line-height: 1.5;
            }
          }

          .hover_mark {
            position: absolute;
            top: 50%;
            right: 50%;
            transform: translate(50%, -50%);
            display: none;
          }

          &:hover {
            background-color: rgba(0, 0, 0, 0.7);
            color: #fff;

            .hover_mark {
              display: block;
            }

            .element-img--wrap::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.7);
            }
          }
        }

        .rank-list-item__element-analysis{
          margin-top: 13px;

          ul{
            float: left;
            width: 50%;

            li {
              margin-top: 12px;
            }

            p {
              display: inline-block;
              vertical-align: top;
              font-size: 13px;
              width: calc(100% - 20px); /* 20px = icon's width */
              min-width: 123px;
              max-width: 175px;

              .title {
                margin-left: 4px;
                color: #666666;
              }

              .value {
                color: #2f2f2f;
                float: right;
              }
            }
          }
        }

        .rank-list-item__element-chart {
          margin-top: 18px;
        }
      }
    }

    .btn__back-to-top {
      cursor: pointer;
      width: 100px;
      margin: 40px auto 0;
      text-align: center;

      p {
        margin-top: 8px;
      }
    }
  }

</style>
